import React from 'react';
import TokenError from 'errors/tokenError';
import { history } from 'utils';
import { appRoutes } from '../../constants';
import i18n from '../../i18n';

/**
 * A TAB RUNNING AN OLDER BUILD THAN THE SERVER (26 Oct 2026: «натискаєш на
 * сторінці, на яку ще не заходив, і таке ловиш… якщо перезавантажити, то буде
 * все гаразд»).
 *
 * Every page is a lazy chunk. A tab keeps the build it was opened with; once
 * the app is rebuilt — every edit under the dev server, every deploy in
 * production — the first visit to a page not yet loaded asks for a chunk the
 * server no longer has (`ChunkLoadError`, «Loading chunk 14 failed»), or gets
 * the new build's chunk whose module ids the old runtime does not know
 * («Cannot read property 'call' of undefined» inside webpack's require).
 * Reloading fetches the current build, which is exactly what the user did by
 * hand.
 */
const STALE_BUILD = [
  /ChunkLoadError/,
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Cannot read propert(y|ies) .*'call'.* of undefined/,
  /reading 'call'/,
];

/** Reload at most once per this window, so a real fault cannot loop. */
export const RELOAD_GUARD_KEY = 'staleBuildReloadAt';
const RELOAD_GUARD_MS = 15000;

export const isStaleBuildError = error => {
  if (!error) {
    return false;
  }
  const text = `${error.name || ''}: ${error.message || ''}`;

  return STALE_BUILD.some(pattern => pattern.test(text));
};

const reloadedRecently = () => {
  try {
    const at = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY));

    return Boolean(at) && Date.now() - at < RELOAD_GUARD_MS;
  } catch (e) {
    // No storage (a private window): never reload automatically.
    return true;
  }
};

const markReload = () => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (e) {
    // Nothing to do — reloadedRecently() already refuses without storage.
  }
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    if (error instanceof TokenError) {
      history.push(appRoutes.AUTH_LOGIN_PAGE);
      return { hasError: false, error: null };
    }

    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Production React swallows boundary-caught errors (no window.onerror),
    // which makes these crashes invisible to monitoring and to e2e traces.
    // The prefixed console line is the ONE durable breadcrumb.
    // eslint-disable-next-line no-console
    console.error(
      '[ErrorBoundary]',
      error,
      errorInfo && errorInfo.componentStack,
    );

    if (isStaleBuildError(error) && !reloadedRecently()) {
      markReload();
      window.location.reload();
    }
  }

  render() {
    const { hasError, error } = this.state;

    if (hasError) {
      // Above the I18nextProvider — the shared i18n singleton is the only
      // translation source available here. A way OUT, not only a verdict:
      // the reload that fixed it by hand, and the message to send on.
      return (
        <div style={{ padding: 24, fontFamily: 'sans-serif' }} data-testid='error-boundary'>
          <h1>{i18n.t('errorPages.somethingWentWrong')}</h1>
          <p>{i18n.t('errorPages.reloadHint')}</p>
          <button
            type='button'
            data-testid='error-boundary-reload'
            onClick={() => window.location.reload()}
            style={{ padding: '8px 16px', fontSize: 16, cursor: 'pointer' }}
          >
            {i18n.t('errorPages.reload')}
          </button>
          {error && error.message && (
            <pre style={{ marginTop: 16, color: '#888', whiteSpace: 'pre-wrap' }} data-testid='error-boundary-message'>
              {error.message}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
